import React from "react";
import { Box, Stack, Tooltip, Button, Option } from "@mui/joy";
import DarkInput from "../../common/DarkInput";
import type {
  AlertModelRule,
  EquipmentAttributeDefinition,
  EquipmentModel,
  FilterCriteria,
} from "../../../api/equipment/Equipment.typed";
import { AlertSeverity } from "../../../api/equipment/Equipment.typed";
import DarkSelect from "../../common/DarkSelect";
import NumericInput from "../../common/NumericInput";
import { getOperatorLabel } from "../../../utils/prefilterHelpers";
import {
  SEdit,
  SDone,
  PlaylistAddIcon,
  DeleteOutlineIcon,
  ToggleOnIcon,
  ToggleOffIcon,
} from "../../../styles/StyledMUI";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import CriteriaBuilder from "./common/CriteriaBuilder";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../../styles/globalStyles";

interface EquipmentModelAlertsEditorProps {
  model: EquipmentModel;
  onModelChange: (model: EquipmentModel) => void;
}

export default function EquipmentModelAlertsEditor({
  model,
  onModelChange,
}: EquipmentModelAlertsEditorProps) {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);
  const alerts: AlertModelRule[] = model.Alerts || [];
  const attrs: EquipmentAttributeDefinition[] = model.Attributes || [];

  const [editingRuleIdx, setEditingRuleIdx] = React.useState<number | null>(null);

  const getSeverityLabelFR = React.useCallback((value: AlertSeverity | string) => {
    let key = "";
    if (typeof value === "string") {
      key = value.toLowerCase();
    } else {
      if (value === AlertSeverity.INFO) key = "info";
      else if (value === AlertSeverity.WARNING) key = "warning";
      else if (value === AlertSeverity.CRITICAL) key = "critical";
    }
    switch (key) {
      case "info":
        return "Info";
      case "warning":
        return "Avertissement";
      case "critical":
        return "Critique";
      default:
        return "Info";
    }
  }, []);

  React.useEffect(() => {
    const arr = model.Alerts || [];
    if (!arr || arr.length === 0) return;
    let changed = false;
    const normalized = arr.map((r: AlertModelRule) => {
      if (!r || (r.Type ?? "").toString().trim().length === 0) {
        changed = true;
        return { ...r, Type: "equipment" };
      }
      return r;
    });
    if (changed) {
      onModelChange({ ...model, Alerts: normalized });
    }
  }, [model, onModelChange]);

  const updateRule = (index: number, patch: Partial<AlertModelRule>) => {
    const next = { ...model };
    next.Alerts = [...(next.Alerts || [])];
    next.Alerts[index] = { ...next.Alerts[index], ...patch };
    onModelChange(next);
  };

  const removeRule = (index: number) => {
    const next = { ...model };
    next.Alerts = (next.Alerts || []).filter((_: AlertModelRule, i: number) => i !== index);
    onModelChange(next);
  };

  const addRule = () => {
    const next = { ...model };
    const firstAttr = (attrs || [])[0];
    const attrType = firstAttr?.Type === "comments" ? "string" : firstAttr?.Type || "string";
    const defaultCrit: FilterCriteria = {
      AttributeID: firstAttr?.ID || "",
      Operator:
        firstAttr &&
        (firstAttr.Type === "string" || firstAttr.Type === "list" || firstAttr.Type === "comments")
          ? "ILIKE"
          : "=",
      Value: "",
      Type: attrType as "number" | "date" | "list" | "string" | "period",
    };
    const newRule: AlertModelRule = {
      ID: crypto.randomUUID(),
      Name: "Alerte",
      Filters: { Criteria: [defaultCrit], Logic: "AND" },
      Operator: "=",
      Value: 0,
      Severity: AlertSeverity.WARNING,
      Type: "equipment",
      Enabled: true,
    };
    next.Alerts = [newRule, ...(next.Alerts || [])];
    onModelChange(next);
    // Enter edit mode on the newly created alert at top
    setEditingRuleIdx(0);
  };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: colorTokens.bg.surface,
        borderRadius: 3,
        border: `1px dashed ${colorTokens.border.default}`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <DarkTypography level="body-xs" colorVariant="muted">
          Règles d'alerte
        </DarkTypography>
        {canManage && (
          <Button
            variant="plain"
            size="sm"
            onClick={addRule}
            sx={{
              color: colorTokens.status.success,
              "&:hover": { backgroundColor: withAlpha(colorTokens.status.success, 0.1) },
            }}
            startDecorator={<PlaylistAddIcon />}
          >
            Ajouter une alerte
          </Button>
        )}
      </Box>

      <Stack spacing={1.5}>
        {alerts.map((rule, idx) => (
          <Box
            key={rule.ID || idx}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              p: 1.5,
              backgroundColor: colorTokens.bg.surfaceMuted,
              borderRadius: 2,
              border: `1px solid ${colorTokens.border.default}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <DarkTypography level="body-xs" colorVariant="accent">
                  Alerte {idx + 1}
                </DarkTypography>
              </Box>
              <Box>
                {editingRuleIdx !== idx ? (
                  <Tooltip title="Modifier">
                    <IconButtonCTA
                      ctaVariant="neutral"
                      aria-label="Modifier alerte"
                      size="sm"
                      variant="plain"
                      onClick={() => setEditingRuleIdx(idx)}
                      sx={{ color: colorTokens.text.secondary, mr: 0.5 }}
                      disabled={!canManage}
                    >
                      <SEdit />
                    </IconButtonCTA>
                  </Tooltip>
                ) : (
                  <Tooltip title="Terminer">
                    <IconButtonCTA
                      ctaVariant="success"
                      aria-label="Terminer édition alerte"
                      size="sm"
                      variant="plain"
                      onClick={() => setEditingRuleIdx(null)}
                      sx={{ color: colorTokens.status.success, mr: 0.5 }}
                    >
                      <SDone />
                    </IconButtonCTA>
                  </Tooltip>
                )}
                <Tooltip title={rule.Enabled ? "Désactiver" : "Activer"}>
                  <IconButtonCTA
                    ctaVariant={rule.Enabled ? "success" : "neutral"}
                    aria-label={rule.Enabled ? "Désactiver alerte" : "Activer alerte"}
                    size="sm"
                    variant="plain"
                    onClick={() => canManage && updateRule(idx, { Enabled: !rule.Enabled })}
                    sx={{
                      color: rule.Enabled ? colorTokens.status.success : colorTokens.text.muted,
                    }}
                    disabled={!canManage}
                  >
                    {rule.Enabled ? (
                      <ToggleOnIcon fontSize="small" />
                    ) : (
                      <ToggleOffIcon fontSize="small" />
                    )}
                  </IconButtonCTA>
                </Tooltip>
                <Tooltip title="Supprimer">
                  <IconButtonCTA
                    ctaVariant="danger"
                    aria-label="Supprimer alerte"
                    size="sm"
                    variant="plain"
                    onClick={() => canManage && removeRule(idx)}
                    sx={{ color: colorTokens.status.danger }}
                    disabled={!canManage}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButtonCTA>
                </Tooltip>
              </Box>
            </Box>

            {editingRuleIdx === idx ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 1 }}>
                <DarkInput
                  placeholder="Nom"
                  value={rule.Name || ""}
                  onChange={(e) => updateRule(idx, { Name: e.target.value })}
                />
              </Box>
            ) : (
              <DarkTypography level="body-sm" colorVariant="secondary" sx={{ fontWeight: 600 }}>
                {rule.Name || "Alerte"}
              </DarkTypography>
            )}

            {editingRuleIdx !== idx ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {(rule.Filters?.Criteria || []).length > 1 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <DarkTypography level="body-xs" colorVariant="muted">
                      Résumé
                    </DarkTypography>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.2,
                        borderRadius: 12,
                        background: colorTokens.bg.surface,
                        color: colorTokens.accent.primary,
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {(rule.Filters?.Criteria || []).length} critères
                    </Box>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.2,
                        borderRadius: 12,
                        background: colorTokens.bg.surface,
                        color: colorTokens.text.secondary,
                        fontSize: 11,
                      }}
                    >
                      Logique: {rule.Filters?.Logic || "AND"}
                    </Box>
                  </Box>
                )}
                {(rule.Filters?.Criteria || []).map((c: FilterCriteria, cIdx: number) => {
                  const a = attrs.find((att) => att.ID === c.AttributeID);
                  return (
                    <Box
                      key={`summary-${cIdx}`}
                      sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
                    >
                      <Box
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: 12,
                          background: colorTokens.bg.surface,
                          color: colorTokens.accent.primary,
                          fontSize: 11,
                        }}
                      >
                        {a?.Label || c.AttributeID}
                      </Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: 12,
                          background: colorTokens.bg.surface,
                          color: colorTokens.text.secondary,
                          fontSize: 11,
                        }}
                      >
                        {getOperatorLabel(c.Operator)}
                      </Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.2,
                          borderRadius: 12,
                          background: colorTokens.accent.primary,
                          color: colorTokens.accent.contrastText,
                          fontSize: 11,
                        }}
                      >
                        {c.Value}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <CriteriaBuilder
                  attributes={attrs}
                  criteria={rule.Filters?.Criteria || []}
                  logic={rule.Filters?.Logic || "AND"}
                  onChange={(next) => {
                    const updated = { ...rule };
                    updated.Filters = updated.Filters || { Criteria: [], Logic: "AND" };
                    updated.Filters.Criteria = next.criteria as FilterCriteria[];
                    updated.Filters.Logic = next.logic as "AND" | "OR";
                    updateRule(idx, updated);
                  }}
                />
              </Box>
            )}

            {editingRuleIdx !== idx ? (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 12,
                    background: colorTokens.bg.surface,
                    color: colorTokens.text.secondary,
                    fontSize: 11,
                  }}
                >
                  Comparateur: {rule.Operator}
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 12,
                    background: colorTokens.bg.surface,
                    color: colorTokens.accent.primary,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  Seuil: {rule.Value}
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 12,
                    background: colorTokens.bg.surface,
                    color: colorTokens.text.secondary,
                    fontSize: 11,
                  }}
                >
                  Gravité: {getSeverityLabelFR(rule.Severity || AlertSeverity.INFO)}
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "grid", gridTemplateColumns: "0.6fr 1fr 0.8fr", gap: 1 }}>
                <DarkSelect
                  placeholder="Comparateur"
                  value={rule.Operator || null}
                  onChange={(_, value) =>
                    updateRule(idx, {
                      Operator: (value as "=" | "!=" | ">" | ">=" | "<" | "<=") || "=",
                    })
                  }
                  size="sm"
                >
                  {["=", "!=", ">", ">=", "<", "<="].map((op) => (
                    <Option key={op} value={op}>
                      {op}
                    </Option>
                  ))}
                </DarkSelect>
                <NumericInput
                  mode="int"
                  placeholder="Seuil"
                  value={String(rule.Value ?? 0)}
                  onValueChange={(next) => updateRule(idx, { Value: parseInt(next || "0", 10) })}
                />
                <DarkSelect
                  placeholder="Gravité"
                  value={(rule.Severity as AlertSeverity) || AlertSeverity.INFO}
                  onChange={(_, value) => updateRule(idx, { Severity: value as AlertSeverity })}
                  size="sm"
                >
                  <Option value={AlertSeverity.INFO}>Info</Option>
                  <Option value={AlertSeverity.WARNING}>Avertissement</Option>
                  <Option value={AlertSeverity.CRITICAL}>Critique</Option>
                </DarkSelect>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
